import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { CheckCircle2, Clock, Plus, Search, XCircle, BadgeCheck, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { JournalEntry, LedgerAccount, PaymentRequest, FiscalPeriod } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const JOURNAL_STATUS_COLORS: Record<string, string> = {
  DRAFT: "border-muted/20 bg-muted/10 text-muted-foreground",
  APPROVED: "border-blue-500/20 bg-blue-500/10 text-blue-600",
  POSTED: "border-green-500/20 bg-green-500/10 text-green-600",
  REVERSED: "border-red-500/20 bg-red-500/10 text-red-600",
  ARCHIVED: "border-muted/20 bg-muted/10 text-muted-foreground",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  DRAFT: "border-muted/20 bg-muted/10 text-muted-foreground",
  SUBMITTED: "border-blue-500/20 bg-blue-500/10 text-blue-600",
  APPROVED: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  REJECTED: "border-red-500/20 bg-red-500/10 text-red-600",
  PAID: "border-green-500/20 bg-green-500/10 text-green-600",
  CANCELLED: "border-muted/20 bg-muted/10 text-muted-foreground",
};

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];

export default function FinancePage() {
  const [accounts, setAccounts] = useState<LedgerAccount[]>();
  const [journals, setJournals] = useState<JournalEntry[]>();
  const [payments, setPayments] = useState<PaymentRequest[]>();
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>();
  const [search, setSearch] = useState("");
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showCreateJournal, setShowCreateJournal] = useState(false);
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [accountForm, setAccountForm] = useState({ account_code: "", account_name: "", account_type: "EXPENSE", currency: "UGX" });
  const [journalForm, setJournalForm] = useState({ fiscal_period_id: "", journal_reference: "", entry_date: new Date().toISOString().slice(0, 10), description: "", debit_account_id: "", credit_account_id: "", amount: "", currency: "UGX" });
  const [paymentForm, setPaymentForm] = useState({ request_reference: "", beneficiary_type: "SUPPLIER", beneficiary_id: "", amount: "", currency: "UGX", purpose: "" });

  const loadAll = async () => {
    try {
      const [accts, jrnls, pays, fps] = await Promise.all([
        services.finance.listAccounts(),
        services.finance.listJournals(),
        services.finance.listPaymentRequests(),
        services.finance.listFiscalPeriods(),
      ]);
      setAccounts(accts.items);
      setJournals(jrnls.items);
      setPayments(pays.items);
      setFiscalPeriods(fps.items);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load finance data";
      toast.error(msg);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleCreateAccount = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await services.finance.createAccount({
        account_code: accountForm.account_code,
        account_name: accountForm.account_name,
        account_type: accountForm.account_type,
        currency: accountForm.currency,
      });
      toast.success("Account created");
      setShowCreateAccount(false);
      setAccountForm({ account_code: "", account_name: "", account_type: "EXPENSE", currency: "UGX" });
      loadAll();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to create account");
    }
  };

  const handleCreateJournal = async (e: FormEvent) => {
    e.preventDefault();
    const debitAcct = accounts?.find(a => a.id === journalForm.debit_account_id);
    const creditAcct = accounts?.find(a => a.id === journalForm.credit_account_id);
    if (!debitAcct || !creditAcct) { toast.error("Select both accounts"); return; }
    const amount = parseFloat(journalForm.amount);
    if (!amount || amount <= 0) { toast.error("Amount must be > 0"); return; }
    if (!journalForm.fiscal_period_id) { toast.error("Select a fiscal period"); return; }
    try {
      const created = await services.finance.createJournal({
        fiscal_period_id: journalForm.fiscal_period_id,
        journal_reference: journalForm.journal_reference,
        entry_date: journalForm.entry_date,
        description: journalForm.description,
        lines: [
          { account_id: debitAcct.id, line_number: 1, debit_amount: amount, credit_amount: 0, currency: journalForm.currency },
          { account_id: creditAcct.id, line_number: 2, debit_amount: 0, credit_amount: amount, currency: journalForm.currency },
        ],
      });
      toast.success("Journal created as DRAFT");
      setShowCreateJournal(false);
      setJournalForm({ fiscal_period_id: "", journal_reference: "", entry_date: new Date().toISOString().slice(0, 10), description: "", debit_account_id: "", credit_account_id: "", amount: "", currency: "UGX" });
      loadAll();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to create journal");
    }
  };

  const handleApproveJournal = async (id: string) => {
    try {
      await services.finance.approveJournal(id);
      toast.success("Journal approved");
      loadAll();
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : "Failed to approve"); }
  };

  const handlePostJournal = async (id: string) => {
    try {
      await services.finance.postJournal(id);
      toast.success("Journal posted");
      loadAll();
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : "Failed to post"); }
  };

  const handleCreatePayment = async (e: FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) { toast.error("Amount must be > 0"); return; }
    try {
      await services.finance.createPaymentRequest({
        request_reference: paymentForm.request_reference,
        beneficiary_type: paymentForm.beneficiary_type,
        beneficiary_id: paymentForm.beneficiary_id || undefined,
        amount,
        currency: paymentForm.currency,
        purpose: paymentForm.purpose,
      });
      toast.success("Payment request created as DRAFT");
      setShowCreatePayment(false);
      setPaymentForm({ request_reference: "", beneficiary_type: "SUPPLIER", beneficiary_id: "", amount: "", currency: "UGX", purpose: "" });
      loadAll();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to create payment request");
    }
  };

  const handleApprovePayment = async (id: string) => {
    try {
      await services.finance.approvePaymentRequest(id);
      toast.success("Payment approved");
      loadAll();
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : "Failed to approve"); }
  };

  const handleRejectPayment = async (id: string) => {
    try {
      await services.finance.rejectPaymentRequest(id);
      toast.success("Payment rejected");
      loadAll();
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : "Failed to reject"); }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await services.finance.markPaymentPaid(id);
      toast.success("Payment marked as paid");
      loadAll();
    } catch (e: unknown) { toast.error(e instanceof Error ? e.message : "Failed to mark paid"); }
  };

  const filteredJournals = journals?.filter(j =>
    !search || j.journalReference.toLowerCase().includes(search.toLowerCase()) ||
    j.description.toLowerCase().includes(search.toLowerCase())
  ) || [];
  const filteredPayments = payments?.filter(p =>
    !search || p.requestReference.toLowerCase().includes(search.toLowerCase()) ||
    p.purpose.toLowerCase().includes(search.toLowerCase())
  ) || [];
  const filteredAccounts = accounts?.filter(a =>
    !search || a.accountCode.toLowerCase().includes(search.toLowerCase()) ||
    a.accountName.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const totalDebit = filteredJournals.reduce((s, j) => s + j.debitTotal, 0);
  const totalCredit = filteredJournals.reduce((s, j) => s + j.creditTotal, 0);
  const pendingPayments = filteredPayments.filter(p => p.status === "DRAFT" || p.status === "SUBMITTED");
  const approvedPayments = filteredPayments.filter(p => p.status === "APPROVED");
  const totalPaymentAmount = filteredPayments.filter(p => p.status === "PAID").reduce((s, p) => s + p.amount, 0);

  if (!accounts || !journals || !payments) return <LoadingState />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Accounts" title="General Ledger & Programme Finance" description="Chart of accounts, journals and payment requests. Farmer settlement and payment control is managed in the Accounts workspace." />

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Debits</p>
                <p className="text-2xl font-bold">{totalDebit.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Credits</p>
                <p className="text-2xl font-bold">{totalCredit.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Payments</p>
                <p className="text-2xl font-bold">{pendingPayments.length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid Total</p>
                <p className="text-2xl font-bold">{totalPaymentAmount.toLocaleString()}</p>
              </div>
              <BadgeCheck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="journals">
        <TabsList>
          <TabsTrigger value="journals">Journal Entries</TabsTrigger>
          <TabsTrigger value="payments">Payment Requests</TabsTrigger>
          <TabsTrigger value="accounts">Chart of Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="journals" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search journals..." value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} className="pl-8" />
            </div>
            <Button onClick={() => setShowCreateJournal(true)}>
              <Plus className="mr-1 h-4 w-4" /> New Journal
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJournals.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No journal entries</TableCell></TableRow>
                ) : filteredJournals.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="font-mono text-sm">{j.journalReference}</TableCell>
                    <TableCell className="text-sm">{j.entryDate}</TableCell>
                    <TableCell className="text-sm">{j.description}</TableCell>
                    <TableCell className="text-right font-mono">{j.debitTotal.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{j.creditTotal.toLocaleString()}</TableCell>
                    <TableCell><Badge variant="outline" className={JOURNAL_STATUS_COLORS[j.status] || ""}>{j.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {j.status === "DRAFT" && <Button size="sm" variant="ghost" onClick={() => handleApproveJournal(j.id)}><CheckCircle2 className="h-4 w-4" /></Button>}
                        {j.status === "APPROVED" && <Button size="sm" variant="ghost" onClick={() => handlePostJournal(j.id)}><BadgeCheck className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search payment requests..." value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} className="pl-8" />
            </div>
            <Button onClick={() => setShowCreatePayment(true)}>
              <Plus className="mr-1 h-4 w-4" /> New Payment Request
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Beneficiary</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payment requests</TableCell></TableRow>
                ) : filteredPayments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-sm">{p.requestReference}</TableCell>
                    <TableCell className="text-sm">{p.beneficiaryType}</TableCell>
                    <TableCell className="text-sm">{p.purpose}</TableCell>
                    <TableCell className="text-right font-mono">{p.amount.toLocaleString()} {p.currency}</TableCell>
                    <TableCell><Badge variant="outline" className={PAYMENT_STATUS_COLORS[p.status] || ""}>{p.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(p.status === "DRAFT" || p.status === "SUBMITTED") && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => handleApprovePayment(p.id)} title="Approve"><CheckCircle2 className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleRejectPayment(p.id)} title="Reject"><XCircle className="h-4 w-4" /></Button>
                          </>
                        )}
                        {p.status === "APPROVED" && <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(p.id)} title="Mark Paid"><DollarSign className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search accounts..." value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} className="pl-8" />
            </div>
            <Button onClick={() => setShowCreateAccount(true)}>
              <Plus className="mr-1 h-4 w-4" /> New Account
            </Button>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No ledger accounts</TableCell></TableRow>
                ) : filteredAccounts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-sm">{a.accountCode}</TableCell>
                    <TableCell className="text-sm">{a.accountName}</TableCell>
                    <TableCell><Badge variant="outline">{a.accountType}</Badge></TableCell>
                    <TableCell className="text-sm">{a.currency || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={a.active ? "border-green-500/20 bg-green-500/10 text-green-600" : "border-muted/20 bg-muted/10 text-muted-foreground"}>{a.active ? "Yes" : "No"}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {showCreateAccount && (
        <Card className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <CardContent className="w-full max-w-md pt-6">
            <h2 className="text-lg font-semibold mb-4">Create Ledger Account</h2>
            <form onSubmit={handleCreateAccount} className="space-y-3">
              <div><Label>Account Code</Label><Input value={accountForm.account_code} onChange={(e: ChangeEvent<HTMLInputElement>) => setAccountForm({ ...accountForm, account_code: e.target.value })} required /></div>
              <div><Label>Account Name</Label><Input value={accountForm.account_name} onChange={(e: ChangeEvent<HTMLInputElement>) => setAccountForm({ ...accountForm, account_name: e.target.value })} required /></div>
              <div><Label>Account Type</Label>
                <Select value={accountForm.account_type} onValueChange={(v: string) => setAccountForm({ ...accountForm, account_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Currency</Label><Input value={accountForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setAccountForm({ ...accountForm, currency: e.target.value })} /></div>
              <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={() => setShowCreateAccount(false)}>Cancel</Button><Button type="submit">Create</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      {showCreateJournal && (
        <Card className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <CardContent className="w-full max-w-lg pt-6">
            <h2 className="text-lg font-semibold mb-4">Create Journal Entry</h2>
            <form onSubmit={handleCreateJournal} className="space-y-3">
              <div><Label>Fiscal Period</Label>
                <Select value={journalForm.fiscal_period_id} onValueChange={(v: string) => setJournalForm({ ...journalForm, fiscal_period_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select period" /></SelectTrigger>
                  <SelectContent>{(fiscalPeriods || []).map((fp) => <SelectItem key={fp.id} value={fp.id}>{fp.periodCode} ({fp.periodName})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Journal Reference</Label><Input value={journalForm.journal_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setJournalForm({ ...journalForm, journal_reference: e.target.value })} required /></div>
              <div><Label>Entry Date</Label><Input type="date" value={journalForm.entry_date} onChange={(e: ChangeEvent<HTMLInputElement>) => setJournalForm({ ...journalForm, entry_date: e.target.value })} required /></div>
              <div><Label>Description</Label><Textarea value={journalForm.description} onChange={(e: ChangeEvent<HTMLInputElement>) => setJournalForm({ ...journalForm, description: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Debit Account</Label>
                  <Select value={journalForm.debit_account_id} onValueChange={(v: string) => setJournalForm({ ...journalForm, debit_account_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.accountCode} — {a.accountName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Credit Account</Label>
                  <Select value={journalForm.credit_account_id} onValueChange={(v: string) => setJournalForm({ ...journalForm, credit_account_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.accountCode} — {a.accountName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" step="0.01" value={journalForm.amount} onChange={(e: ChangeEvent<HTMLInputElement>) => setJournalForm({ ...journalForm, amount: e.target.value })} required /></div>
                <div><Label>Currency</Label><Input value={journalForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setJournalForm({ ...journalForm, currency: e.target.value })} /></div>
              </div>
              <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={() => setShowCreateJournal(false)}>Cancel</Button><Button type="submit">Create Draft</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      {showCreatePayment && (
        <Card className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <CardContent className="w-full max-w-md pt-6">
            <h2 className="text-lg font-semibold mb-4">Create Payment Request</h2>
            <form onSubmit={handleCreatePayment} className="space-y-3">
              <div><Label>Request Reference</Label><Input value={paymentForm.request_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, request_reference: e.target.value })} required /></div>
              <div><Label>Beneficiary Type</Label>
                <Select value={paymentForm.beneficiary_type} onValueChange={(v: string) => setPaymentForm({ ...paymentForm, beneficiary_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SUPPLIER">Supplier</SelectItem>
                    <SelectItem value="EMPLOYEE">Employee</SelectItem>
                    <SelectItem value="FARMER">Farmer</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Beneficiary ID (optional)</Label><Input value={paymentForm.beneficiary_id} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, beneficiary_id: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required /></div>
                <div><Label>Currency</Label><Input value={paymentForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, currency: e.target.value })} /></div>
              </div>
              <div><Label>Purpose</Label><Textarea value={paymentForm.purpose} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, purpose: e.target.value })} required /></div>
              <div className="flex gap-2 justify-end"><Button type="button" variant="outline" onClick={() => setShowCreatePayment(false)}>Cancel</Button><Button type="submit">Create Draft</Button></div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
